import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardContent } from '../../../components/Card';
import { Button } from '../../../components/Button';
import { Input } from '../../../components/Input';
import { Badge } from '../../../components/Badge';
import { Settings, Shield, Bell, Users, Mail, Link as LinkIcon, Database, Loader2, AlertCircle, CheckCircle2, Download, ExternalLink } from 'lucide-react';
import toast from 'react-hot-toast';
import apiClient from '../../../lib/api';
import { LeaveTypesManager } from './LeaveTypesManager';
import { ReportingManagers } from './ReportingManagers';

const ALL_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

type SettingTab = 'general' | 'org' | 'permissions' | 'notifications' | 'templates' | 'integrations' | 'export';

interface SettingsData {
  companyName: string;
  timezone: string;
  currency: string;
  workStart: string;
  workEnd: string;
  workingDays: string[];
  leaveQuotas: { type: string; days: number }[];
  // Extended notification & org preferences
  notifyLeaveRequests?: boolean;
  notifyAttendanceAlerts?: boolean;
  notifyWeeklyDigest?: boolean;
  requireManagerLeaveApproval?: boolean;
  requireManagerAttendanceApproval?: boolean;
  defaultRole?: string;
  slackWebhookUrl?: string;
  welcomeTemplate?: string;
  leaveApprovalTemplate?: string;
}

const EMPTY: SettingsData = {
  companyName: 'Trusty Bytes',
  timezone: 'Asia/Kolkata',
  currency: 'INR (₹)',
  workStart: '09:00',
  workEnd: '17:00',
  workingDays: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'],
  leaveQuotas: [
    { type: 'Annual Leave', days: 20 },
    { type: 'Sick Leave', days: 10 },
    { type: 'Parental Leave', days: 90 },
  ],
  notifyLeaveRequests: true,
  notifyAttendanceAlerts: true,
  notifyWeeklyDigest: false,
  requireManagerLeaveApproval: true,
  requireManagerAttendanceApproval: true,
  defaultRole: 'employee',
  slackWebhookUrl: '',
  welcomeTemplate: 'Welcome to {{company_name}}, {{employee_name}}! We are excited to have you on board.',
  leaveApprovalTemplate: 'Hello {{employee_name}}, your {{leave_type}} request from {{start_date}} to {{end_date}} has been approved.',
};

export function HrSettings() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<SettingTab>('general');
  const [form, setForm] = useState<SettingsData>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    apiClient.get('/people/settings')
      .then((res) => { if (active) setForm({ ...EMPTY, ...(res.data?.data ?? res.data) }); })
      .catch((e) => { if (active) setError(e?.response?.data?.message || e.message); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  const update = (patch: Partial<SettingsData>) => setForm((f) => ({ ...f, ...patch }));

  const toggleDay = (day: string) => {
    setForm((f) => ({
      ...f,
      workingDays: f.workingDays.includes(day)
        ? f.workingDays.filter((d) => d !== day)
        : [...f.workingDays, day],
    }));
  };

  const updateQuota = (idx: number, days: number) => {
    setForm((f) => ({
      ...f,
      leaveQuotas: f.leaveQuotas.map((q, i) => (i === idx ? { ...q, days } : q)),
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await apiClient.put('/people/settings', form);
      setForm((f) => ({ ...f, ...(res.data?.data ?? res.data) }));
      toast.success('Settings saved successfully');
    } catch (e: any) {
      setError(e?.response?.data?.message || e.message);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const exportCsv = async (endpoint: string, filename: string) => {
    try {
      const res = await apiClient.get(endpoint);
      const data = res.data?.data ?? res.data ?? [];
      if (!Array.isArray(data) || data.length === 0) {
        toast.error('No records available to export.');
        return;
      }
      const headers = Object.keys(data[0]).join(',');
      const rows = data.map((row: any) => Object.values(row).map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','));
      const csvContent = 'data:text/csv;charset=utf-8,' + [headers, ...rows].join('\n');
      const encodedUri = encodeURI(csvContent);
      const link = document.createElement('a');
      link.setAttribute('href', encodedUri);
      link.setAttribute('download', `${filename}_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`Exported ${filename} successfully!`);
    } catch (e: any) {
      toast.error('Failed to export data: ' + (e.message || 'Error occurred'));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-muted-foreground">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading settings…
      </div>
    );
  }

  const tabs: { id: SettingTab; label: string; icon: React.ReactNode }[] = [
    { id: 'general', label: 'General', icon: <Settings className="w-4 h-4 mr-2" /> },
    { id: 'org', label: 'Org Structure', icon: <Users className="w-4 h-4 mr-2" /> },
    { id: 'permissions', label: 'Permissions & Roles', icon: <Shield className="w-4 h-4 mr-2" /> },
    { id: 'notifications', label: 'Notifications', icon: <Bell className="w-4 h-4 mr-2" /> },
    { id: 'templates', label: 'Email Templates', icon: <Mail className="w-4 h-4 mr-2" /> },
    { id: 'integrations', label: 'Integrations', icon: <LinkIcon className="w-4 h-4 mr-2" /> },
    { id: 'export', label: 'Data Export', icon: <Database className="w-4 h-4 mr-2" /> },
  ];

  return (
    <div className="space-y-6 max-w-5xl p-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="tracking-tight text-foreground font-display text-page-title">HR Settings</h1>
          <p className="text-muted-foreground text-sm">Configure HR workflows, access controls, notifications, and integrations.</p>
        </div>
        <Button variant="primary" onClick={handleSave} disabled={saving}>
          {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          {saving ? 'Saving…' : 'Save Changes'}
        </Button>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-sm text-destructive">
          <AlertCircle className="w-4 h-4" /> {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {/* Left Navigation Sidebar */}
        <div className="md:col-span-1 space-y-1 bg-card p-2 rounded-lg border border-border h-fit">
          {tabs.map((tab) => {
            const active = activeTab === tab.id;
            return (
              <Button
                key={tab.id}
                variant="ghost"
                onClick={() => setActiveTab(tab.id)}
                className={`w-full justify-start text-sm transition-colors ${
                  active ? 'bg-primary/10 text-primary font-semibold' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {tab.icon} {tab.label}
              </Button>
            );
          })}
        </div>

        {/* Right Active Tab Content */}
        <div className="md:col-span-3 space-y-6">
          {/* TAB 1: GENERAL */}
          {activeTab === 'general' && (
            <>
              <Card>
                <CardHeader>
                  <h3 className="text-foreground font-display text-section-title">Company Information</h3>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">Company Name</label>
                    <Input value={form.companyName} onChange={(e) => update({ companyName: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">Timezone</label>
                    <Input value={form.timezone} onChange={(e) => update({ timezone: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">Default Currency</label>
                    <Input value={form.currency} onChange={(e) => update({ currency: e.target.value })} />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <h3 className="text-foreground font-display text-section-title">Work Week Configuration</h3>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-secondary/20 rounded-md border border-border">
                    <div>
                      <p className="font-medium text-sm text-foreground">Standard Working Hours</p>
                      <p className="text-xs text-muted-foreground">Default hours for new employees</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Input value={form.workStart} type="time" className="w-24" onChange={(e) => update({ workStart: e.target.value })} />
                      <span className="text-muted-foreground text-sm">to</span>
                      <Input value={form.workEnd} type="time" className="w-24" onChange={(e) => update({ workEnd: e.target.value })} />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">Working Days</label>
                    <div className="flex gap-2">
                      {ALL_DAYS.map((day) => {
                        const on = form.workingDays.includes(day);
                        return (
                          <div
                            key={day}
                            onClick={() => toggleDay(day)}
                            className={`flex-1 py-2 text-center text-sm rounded cursor-pointer border transition-colors ${
                              on ? 'bg-primary/10 border-primary/30 text-primary font-medium' : 'bg-card border-border text-muted-foreground'
                            }`}
                          >
                            {day}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <h3 className="text-foreground font-display text-section-title">Leave Types & Quotas</h3>
                  <p className="text-xs text-muted-foreground mt-0.5">The master leave catalogue. Changes here apply to new allocations; existing balances are unaffected.</p>
                </CardHeader>
                <CardContent>
                  <LeaveTypesManager />
                </CardContent>
              </Card>
            </>
          )}

          {/* TAB 2: ORG STRUCTURE */}
          {activeTab === 'org' && (
            <>
            <Card>
              <CardHeader>
                <h3 className="text-foreground font-display text-section-title">Organization & Hierarchy Settings</h3>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="p-4 border border-border rounded-lg bg-card space-y-2">
                    <h4 className="font-medium text-sm text-foreground flex items-center justify-between">
                      Organizational Chart <ExternalLink className="w-4 h-4 text-muted-foreground" />
                    </h4>
                    <p className="text-xs text-muted-foreground">View and manage company reporting lines and departments.</p>
                    <Button variant="secondary" size="sm" onClick={() => navigate('/people/orgchart')}>
                      Open Org Chart
                    </Button>
                  </div>
                  <div className="p-4 border border-border rounded-lg bg-card space-y-2">
                    <h4 className="font-medium text-sm text-foreground flex items-center justify-between">
                      Office Locations <ExternalLink className="w-4 h-4 text-muted-foreground" />
                    </h4>
                    <p className="text-xs text-muted-foreground">Configure geofencing radii, office locations, and GPS parameters.</p>
                    <Button variant="secondary" size="sm" onClick={() => navigate('/people/attendance?tab=settings')}>
                      Manage Locations & Policy
                    </Button>
                  </div>
                </div>

                <div className="space-y-4 pt-4 border-t border-border">
                  <h4 className="font-medium text-sm text-foreground">Approval Hierarchy Rules</h4>
                  <div className="flex items-center justify-between p-3 bg-secondary/10 rounded-md border border-border">
                    <div>
                      <p className="text-sm font-medium text-foreground">Require Reporting Manager for Leave Requests</p>
                      <p className="text-xs text-muted-foreground">Requests route to direct manager before HR review.</p>
                    </div>
                    <input
                      type="checkbox"
                      className="size-4 accent-primary cursor-pointer"
                      checked={form.requireManagerLeaveApproval}
                      onChange={(e) => update({ requireManagerLeaveApproval: e.target.checked })}
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 bg-secondary/10 rounded-md border border-border">
                    <div>
                      <p className="text-sm font-medium text-foreground">Require Manager Review for Attendance Adjustments</p>
                      <p className="text-xs text-muted-foreground">Regularisation requests require reporting manager sign-off.</p>
                    </div>
                    <input
                      type="checkbox"
                      className="size-4 accent-primary cursor-pointer"
                      checked={form.requireManagerAttendanceApproval}
                      onChange={(e) => update({ requireManagerAttendanceApproval: e.target.checked })}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <ReportingManagers />
            </>
          )}

          {/* TAB 3: PERMISSIONS & ROLES */}
          {activeTab === 'permissions' && (
            <Card>
              <CardHeader>
                <h3 className="text-foreground font-display text-section-title">Access Control & Security</h3>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg flex items-start justify-between gap-4">
                  <div>
                    <h4 className="font-medium text-sm text-foreground">Role-Based Access Control (RBAC)</h4>
                    <p className="text-xs text-muted-foreground mt-1">Configure permissions for Super Admin, HR Admin, Manager, and Employee roles.</p>
                  </div>
                  <Badge variant="primary">Enforced by Auth Service</Badge>
                </div>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">Default Role for New Hires</label>
                    <select
                      className="w-full h-10 px-3 rounded-md border border-border bg-card text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                      value={form.defaultRole}
                      onChange={(e) => update({ defaultRole: e.target.value })}
                    >
                      <option value="employee">Employee (Self Service)</option>
                      <option value="reporting_manager">Reporting Manager</option>
                      <option value="hr_admin">HR Admin</option>
                    </select>
                  </div>
                  <div className="p-3 bg-secondary/10 border border-border rounded-md text-xs text-muted-foreground space-y-1">
                    <p className="font-medium text-foreground">Permission Scopes Summary:</p>
                    <p>• <strong>Employee:</strong> Self-scope access to attendance, leaves, shifts, and own payroll slips.</p>
                    <p>• <strong>Manager:</strong> Team-scope access for approving attendance & leave requests.</p>
                    <p>• <strong>HR / Admin:</strong> Company-wide scope across all employees, settings, and reports.</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* TAB 4: NOTIFICATIONS */}
          {activeTab === 'notifications' && (
            <Card>
              <CardHeader>
                <h3 className="text-foreground font-display text-section-title">System & Alert Preferences</h3>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-secondary/10 rounded-md border border-border">
                  <div>
                    <p className="text-sm font-medium text-foreground">Leave Application Alerts</p>
                    <p className="text-xs text-muted-foreground">Notify managers when employees submit leave requests.</p>
                  </div>
                  <input
                    type="checkbox"
                    className="size-4 accent-primary cursor-pointer"
                    checked={form.notifyLeaveRequests}
                    onChange={(e) => update({ notifyLeaveRequests: e.target.checked })}
                  />
                </div>

                <div className="flex items-center justify-between p-3 bg-secondary/10 rounded-md border border-border">
                  <div>
                    <p className="text-sm font-medium text-foreground">Attendance Exception Notifications</p>
                    <p className="text-xs text-muted-foreground">Send alerts for missing check-outs or location policy violations.</p>
                  </div>
                  <input
                    type="checkbox"
                    className="size-4 accent-primary cursor-pointer"
                    checked={form.notifyAttendanceAlerts}
                    onChange={(e) => update({ notifyAttendanceAlerts: e.target.checked })}
                  />
                </div>

                <div className="flex items-center justify-between p-3 bg-secondary/10 rounded-md border border-border">
                  <div>
                    <p className="text-sm font-medium text-foreground">Weekly HR Digest Report</p>
                    <p className="text-xs text-muted-foreground">Email weekly attendance summaries and upcoming holiday/leave lists.</p>
                  </div>
                  <input
                    type="checkbox"
                    className="size-4 accent-primary cursor-pointer"
                    checked={form.notifyWeeklyDigest}
                    onChange={(e) => update({ notifyWeeklyDigest: e.target.checked })}
                  />
                </div>
              </CardContent>
            </Card>
          )}

          {/* TAB 5: EMAIL TEMPLATES */}
          {activeTab === 'templates' && (
            <Card>
              <CardHeader>
                <h3 className="text-foreground font-display text-section-title">Email & Notice Templates</h3>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">Welcome Email (New Employee Onboarding)</label>
                  <textarea
                    rows={4}
                    className="w-full p-3 rounded-md border border-border bg-card text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                    value={form.welcomeTemplate}
                    onChange={(e) => update({ welcomeTemplate: e.target.value })}
                  />
                  <p className="text-xs text-muted-foreground">Available placeholders: <code>{"{{employee_name}}"}</code>, <code>{"{{company_name}}"}</code></p>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">Leave Approval Notification</label>
                  <textarea
                    rows={4}
                    className="w-full p-3 rounded-md border border-border bg-card text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                    value={form.leaveApprovalTemplate}
                    onChange={(e) => update({ leaveApprovalTemplate: e.target.value })}
                  />
                  <p className="text-xs text-muted-foreground">Available placeholders: <code>{"{{employee_name}}"}</code>, <code>{"{{leave_type}}"}</code>, <code>{"{{start_date}}"}</code>, <code>{"{{end_date}}"}</code></p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* TAB 6: INTEGRATIONS */}
          {activeTab === 'integrations' && (
            <Card>
              <CardHeader>
                <h3 className="text-foreground font-display text-section-title">Third-Party Service Integrations</h3>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-3 p-4 border border-border rounded-lg bg-card">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="p-2 bg-primary/10 text-primary rounded-md"><LinkIcon className="w-5 h-5" /></div>
                      <div>
                        <h4 className="font-medium text-sm text-foreground">Slack / Microsoft Teams Webhook</h4>
                        <p className="text-xs text-muted-foreground">Broadcast check-in anomalies and leave updates to a channel.</p>
                      </div>
                    </div>
                    <Badge variant="muted">Optional</Badge>
                  </div>
                  <Input
                    placeholder="https://hooks.slack.com/services/..."
                    value={form.slackWebhookUrl || ''}
                    onChange={(e) => update({ slackWebhookUrl: e.target.value })}
                  />
                </div>

                <div className="flex items-center justify-between p-4 border border-border rounded-lg bg-card">
                  <div className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-success" />
                    <div>
                      <h4 className="font-medium text-sm text-foreground">PostgreSQL 18 & REST API Gateway</h4>
                      <p className="text-xs text-muted-foreground">Connected on port 3001/3003 with live schema qualification.</p>
                    </div>
                  </div>
                  <Badge variant="success">Active</Badge>
                </div>
              </CardContent>
            </Card>
          )}

          {/* TAB 7: DATA EXPORT */}
          {activeTab === 'export' && (
            <Card>
              <CardHeader>
                <h3 className="text-foreground font-display text-section-title">Data Export & Reporting Backups</h3>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-xs text-muted-foreground">Download instant CSV records of employee datasets for auditing or offline payroll computation.</p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                  <div className="p-4 border border-border rounded-lg bg-card space-y-3">
                    <div>
                      <h4 className="font-medium text-sm text-foreground">Employee Directory</h4>
                      <p className="text-xs text-muted-foreground">Full roster with employee codes, emails, and departments.</p>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => exportCsv('/people/employees', 'Employees_Master')}>
                      <Download className="w-4 h-4 mr-2" /> Download CSV
                    </Button>
                  </div>

                  <div className="p-4 border border-border rounded-lg bg-card space-y-3">
                    <div>
                      <h4 className="font-medium text-sm text-foreground">Attendance Logs</h4>
                      <p className="text-xs text-muted-foreground">Raw check-in/out timestamps and calculated working hours.</p>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => exportCsv('/people/attendance/admin/list', 'Attendance_Sessions')}>
                      <Download className="w-4 h-4 mr-2" /> Download CSV
                    </Button>
                  </div>

                  <div className="p-4 border border-border rounded-lg bg-card space-y-3">
                    <div>
                      <h4 className="font-medium text-sm text-foreground">Leave Requests History</h4>
                      <p className="text-xs text-muted-foreground">Applied leaves, approvals, balances, and dates.</p>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => exportCsv('/people/leave/requests', 'Leave_Requests')}>
                      <Download className="w-4 h-4 mr-2" /> Download CSV
                    </Button>
                  </div>

                  <div className="p-4 border border-border rounded-lg bg-card space-y-3">
                    <div>
                      <h4 className="font-medium text-sm text-foreground">Payroll Records</h4>
                      <p className="text-xs text-muted-foreground">Salary breakdowns, deductions, and net payouts.</p>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => exportCsv('/people/payroll', 'Payroll_Records')}>
                      <Download className="w-4 h-4 mr-2" /> Download CSV
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

